import { NgClass, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  Input,
  input,
  InputSignal,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { NgApexchartsModule } from 'ng-apexcharts';
import { PDropdownOptions } from '../../../interfaces';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { BaseDashboardChartComponent } from '../base-dashboard-chart.component';
import { ChartCardComponent } from "../chart-card/chart-card.component";
import { SpinnerComponent } from '../../spinner/spinner.component';
import { TranslateService } from '@ngx-translate/core';
import { Tooltip } from 'primeng/tooltip';
import {TranslatePipe} from "@ngx-translate/core";

export type BoxChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  legend: ApexLegend;
  tooltip: Tooltip;
}

@Component({
  selector: 'co-box-plot',
  imports: [NgApexchartsModule, SelectModule, FormsModule, ChartCardComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './box-plot.component.html',
  styleUrl: './box-plot.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BoxPlotComponent extends BaseDashboardChartComponent implements OnInit {
  data: InputSignal<any[]> = input.required();
  // chartOptions!: WritableSignal<ChartOptions>;
  // chartOptions: any = signal({});
  chartOptions: any = signal({});
  chartSeries: any = signal([]);
  isXLabels: InputSignal<boolean> = input(false);
  isYLabels: InputSignal<boolean> = input(false);
  isLegend: InputSignal<boolean> = input(false);
  xAxisTitle = input<string>('');
  yAxisTitle = input<string>('');
  chartReady = input.required<boolean>();
  hoverLabels: InputSignal<any[]> = input.required();
  tooltipFormatter: InputSignal<(label: any, seriesIndex: number) => string> = input.required();

  @Input() ChartStats?: string;
  //@Output() filterChanged = new EventEmitter<Record<string,any>>();



  constructor(private translate : TranslateService) {
    super();
    effect(() => {
      const ready = this.chartReady();
      const data = this.data();
      if(ready && data.length>0)
        this.updateChartOptions();
    })
  }

  ngOnInit(): void {
    // this.updateChartOptions();
  }

  updateChartOptions(): void {
    const showHistoricalSeries = this.data().some((item) => item.showHistorical !== false);
    const series = [
      {
        name: this.translate.instant('ICD10.TOOLTIP.CURRENT'),
        data: this.data().map((item) => ({
          x: item.title,
          y: [item.minCurrent,
          item.q1Current,
          item.medianReductionCurrent,
          item.q3Current,
          item.maxCurrent
          ]
        }))
      },
      ...(showHistoricalSeries ? [{
        name: this.translate.instant('ICD10.TOOLTIP.HISTORICAL'),
        data: this.data().map((item) => ({
          x: item.title,
          y: [item.minHistorical,
          item.q1Historical,
          item.medianReductionHistorical,
          item.q3Historical,
          item.maxHistorical
          ]
        }))
      }] : [])
    ];

    this.chartOptions.set({
      series,
      chart: {
        width: '100%',
        type: 'boxPlot',
         animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
          zoomedArea: {
            fill: { color: '#90CAF9', opacity: 0.4 },
            stroke: { color: '#0D47A1', opacity: 0.4, width: 1 },
          },
        },
        toolbar: {
          show: true,
          tools: { download: true, zoom: true, zoomin: false, zoomout: false, pan: false, reset: true },
        },
        events: {
          mounted: (chartContext: { el: HTMLElement; }) => this.applyBoxColors(chartContext.el),
          updated: (chartContext: { el: HTMLElement; }) => this.applyBoxColors(chartContext.el)
        }
      },
      tooltip: {
  enabled: true,
  shared: false,
  custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
    const label = this.hoverLabels()[dataPointIndex];
    const formatter = this.tooltipFormatter();
    if (label && formatter) return formatter(label, seriesIndex);

    const y = w.config.series[seriesIndex].data[dataPointIndex].y;
    const t = this.translate;
    return `
      <div style="padding:10px;font-size:12px;background:white;border:1px solid #ccc;">
        <strong>${t.instant('ICD10.TOOLTIP.MINIMUM')}:</strong> ${y[0]}<br/>
        <strong>${t.instant('ICD10.TOOLTIP.Q1')}:</strong> ${y[1]}<br/>
        <strong>${t.instant('ICD10.TOOLTIP.MEDIAN')}:</strong> ${y[2]}<br/>
        <strong>${t.instant('ICD10.TOOLTIP.Q3')}:</strong> ${y[3]}<br/>
        <strong>${t.instant('ICD10.TOOLTIP.MAXIMUM')}:</strong> ${y[4]}
      </div>
    `;
  }
},

      plotOptions: {
        boxPlot: {
          colors: {
            upper: 'rgba(0,0,0,0)',   // same color = solid box
            lower: 'rgba(0,0,0,0)'
          }
        }
      },
      stroke: {
        width: 2,
        colors: ['#125773', '#7978E9'], // series 0 (Current), series 1 (Historical)
      },
      colors: ['#125773', '#7978E9'],
      legend: {
        show: this.isLegend(),
        position: 'bottom',
        floating: false,
        markers: {
          width: 12,
          height: 12,
          fillColors: ['rgba(7, 62, 107, 0.6)', 'rgba(76, 8, 88, 0.6)']
        },
        itemMargin: {
          horizontal: 5,
          vertical: 5,
        },
      },
      xaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          show: this.isXLabels(),
          formatter: (val: number) => this.formatNumberShort(val)
        },
        title: {
          text: this.xAxisTitle(),
        }
      },
      yaxis: {
        labels: {
          show: this.isYLabels(),
          formatter: (val: number) => this.formatNumberShort(val)
        },
        title: {
          text: this.yAxisTitle(),
        }
      },
      // tooltip: {
      //   enabled: true,
      //   intersect: true,
      //   shared: false,
      //   followCursor: true,
      //   custom: (opts: any) => {
      //     const label = this.hoverLabels()[opts.dataPointIndex];
      //     const formatter = this.tooltipFormatter();
      //     if (!label || !formatter) return '';
      //     return formatter(label, opts.seriesIndex);
      //   }
      // }
    });
    
  }

  getColors(color: string = '') {
    if (color == '') {
      return [
        '#4B49AC',
        '#98BDFF',
        '#7DA0FA',
        '#7978E9',
        '#125773',
        '#27CDF2',
        '#488A99',
        '#C6D936',
      ];
    } else return [color];
  }

  applyBoxColors(containerEl: HTMLElement) {
    const seriesGroups = containerEl.querySelectorAll<SVGGElement>('.apexcharts-series');
    seriesGroups.forEach((seriesG, index) => {
      const fillColor = index === 0 ? 'rgba(7, 62, 107, 0.6)' : 'rgba(76, 8, 88, 0.6)';
      const strokeColor = index === 0 ? '#125773' : '#7978E9'; // fully opaque outline
      seriesG.querySelectorAll<SVGRectElement>('rect, path').forEach(el => {
        el.setAttribute('fill', fillColor);
        el.setAttribute('stroke', strokeColor);
      });
      seriesG.querySelectorAll<SVGLineElement>('line').forEach(line => {
        line.setAttribute('stroke', strokeColor);
      });
    });
  }

  formatNumberShort(n: number): string {
    if (typeof n !== 'number') {
      return n;
    }

    if (n >= 1000000000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (n >= 1000000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toString();
  }
}
